import type { AutoPublishState } from "../../hooks/useAutoPublish";
import type { AutoPublishProject } from "../../hooks/useAutoPublishProjects";
import type { PublicationModel } from "../../types/models";
import { getAutoPublishSourceFailure, isAutoPostWaitingForShorts } from "./autoPostStatus";

/** Empty failed sources cannot post, but their saved settings survive recovery. */
export const isAutoPublishProjectBlocked = (project: AutoPublishProject): boolean =>
  Boolean(getAutoPublishSourceFailure(project.taskStatus)) &&
  project.hasReadyShorts !== true && project.hasQueuedPosts !== true;

/** The visible switch and aggregate activity describe what can currently run. */
export const isAutoPublishProjectActive = (project: AutoPublishProject): boolean =>
  project.autoPublish?.enabled === true && !isAutoPublishProjectBlocked(project);

/** The nav, Calendar button and project list all use the same project snapshot. */
export const getActiveAutoPublishProjectCount = (projects: readonly AutoPublishProject[]) =>
  projects.filter(isAutoPublishProjectActive).length;

export type CalendarAutoPublishQueue = Pick<AutoPublishState, "upcoming" | "nextPostAt">;

/** Waiting ends as soon as a booking exists, even if the source is still processing. */
export const isAutoPublishProjectWaiting = (project: AutoPublishProject, queue?: CalendarAutoPublishQueue): boolean =>
  isAutoPostWaitingForShorts({
    enabled: isAutoPublishProjectActive(project),
    taskStatus: project.taskStatus,
    upcoming: queue?.upcoming,
    hasQueuedPosts: project.hasQueuedPosts,
  });

/** Match the settings query: scheduled Auto posts remain queued until their status changes. */
export function getCalendarAutoPublishQueues(
  publications: readonly PublicationModel[] | undefined,
): Map<string, CalendarAutoPublishQueue> {
  const queues = new Map<string, CalendarAutoPublishQueue>();
  for (const publication of publications ?? []) {
    if (!publication.autoFolderId || publication.status !== "scheduled") continue;
    const queue = queues.get(publication.autoFolderId) ?? { upcoming: 0, nextPostAt: null };
    queue.upcoming += 1;
    if (!queue.nextPostAt || publication.scheduledAt.getTime() < queue.nextPostAt.getTime()) {
      queue.nextPostAt = publication.scheduledAt;
    }
    queues.set(publication.autoFolderId, queue);
  }
  return queues;
}
